import { forwardRef, useImperativeHandle } from 'react';

import { iaRa, optionify } from 'lib/helpers/utility';
import { PAYER_TYPES } from 'lib/constants/payer/payerTypes';
import { useStates } from 'lib/hooks/queries/states/useStates';

import Input from 'components/shared/Input/Input';
import Select from 'components/shared/Select/Select';
import { Alert } from 'components/shared/Alert/Alert';
import { handleModifierSearch } from 'components/practice/charts/ClinicalNote/lib/cptAndIcdHelpers';

import { customSelectStyle } from '../lib/styles';
import {
  LIMITED_ELECTRONIC_SERVICES_MESSAGE,
  LIMITED_ELECTRONIC_SERVICES_TITLE
} from '../lib/constants';

import { findPropertyByValue } from './lib/helpers';
import InOutNetworkProviders from './InOutNetworkProviders/InOutNetworkProviders';

const PayerForm = forwardRef(({ formik, isUpdate, practiceId, isLoading, openModal }, ref) => {
  const { data: statesData } = useStates();

  const states = optionify(iaRa(statesData?.states), 'name', 'id');

  const { setFieldValue, values, errors, handleChange } = formik;

  useImperativeHandle(ref, () => ({
    submitForm: () => formik.handleSubmit(),
    isLoading
  }));

  return (
    <form onSubmit={formik.handleSubmit} className="flex flex-col gap-4 pb-5">
      {!isUpdate && (
        <Alert
          width="full"
          color="warning"
          variant="lighter"
          title={LIMITED_ELECTRONIC_SERVICES_TITLE}
          message={LIMITED_ELECTRONIC_SERVICES_MESSAGE}
        />
      )}

      <div className="flex flex-col gap-1">
        <p className="text-base font-500 text-neutral-900">Basic information</p>
        <p className="text-sm text-neutral-700">
          Define the payer&apos;s main details and contact information.
        </p>
      </div>

      <div className="grid grid-cols-3 gap-4">
        <Input
          label="Name"
          placeholder="Enter name"
          name="name"
          value={values.name}
          onChange={handleChange}
          id="name"
          error={errors.name}
        />
        <Input
          label="Phone number"
          isOptional
          placeholder="Enter phone number"
          name="phone"
          value={values.phone}
          onChange={handleChange}
          id="phoneNumber"
          error={errors.phone}
        />
        <Input
          label="Address 1"
          isOptional
          placeholder="Enter primary address"
          name="address_ln_1"
          value={values.address_ln_1}
          onChange={handleChange}
          id="address_ln_1"
          error={errors.address_ln_1}
        />
        <Input
          label="Address 2"
          isOptional
          placeholder="Enter secondary address"
          name="address_ln_2"
          value={values.address_ln_2}
          onChange={handleChange}
          id="address_ln_2"
        />
        <Input
          label="City"
          isOptional
          placeholder="Enter city"
          name="city"
          value={values.city}
          onChange={handleChange}
          id="city"
        />
        <Select
          label="State"
          isOptional
          className="select-full-width w-full"
          placeholder="Type and select state"
          value={findPropertyByValue(states, values.state)}
          options={states}
          onChange={(v) => {
            setFieldValue('state', v?.value || null);
          }}
          styles={customSelectStyle}
          inputId="stateAddress"
          error={errors.state || null}
          menuPortalTarget={document.body}
        />
        <Input
          label="Zip code"
          isOptional
          placeholder="Enter zip code"
          name="zip"
          value={values.zip}
          onChange={handleChange}
          id="zip"
          error={errors.zip}
        />
        <Select
          label="Type"
          className="select-full-width w-full"
          placeholder="Select type"
          value={findPropertyByValue(PAYER_TYPES, values.type)}
          options={PAYER_TYPES}
          onChange={(v) => {
            setFieldValue('type', v?.value || '');
          }}
          styles={customSelectStyle}
          isClearable={false}
          error={errors.type || null}
          menuPortalTarget={document.body}
        />
        <Select
          className="select-full-width w-full"
          label="Virtual modifiers"
          isOptional
          placeholder="Type and select modifiers"
          noOptionsMessage={() => 'Start typing to search for modifiers'}
          isAsync
          isMulti
          isClearable
          value={values.virtual_modifiers?.map((modifier) => ({
            label: modifier,
            value: modifier
          }))}
          loadOptions={handleModifierSearch}
          menuPortalTarget={document.body}
          styles={customSelectStyle}
          error={errors.virtual_modifiers || null}
          onChange={(selectedOptions) => {
            setFieldValue(
              'virtual_modifiers',
              selectedOptions ? selectedOptions.map((option) => option.value) : []
            );
          }}
        />

        <InOutNetworkProviders
          values={values}
          practiceId={practiceId}
          setFieldValue={setFieldValue}
          openModal={openModal}
        />
      </div>
    </form>
  );
});

PayerForm.displayName = 'PayerForm';

export default PayerForm;
